import { useState } from "react";
import { useWatch } from "react-hook-form";
import type { Control } from "react-hook-form";
import { Check, ChevronsUpDown } from "lucide-react";
import { useListInventoryItems } from "@workspace/api-client-react";
import type { InventoryItem } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { specsToLine } from "@/lib/specs";
import { defaultQtyPerServe, stockOutHint, stockOutUnitLabel } from "@/lib/stock-out-basis";
import { cn } from "@/lib/utils";

interface Props {
  index: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: (name: string, value: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: (name: string, options?: object) => object;
  /** The current item ID in edit mode — exclude from ingredient picker to prevent self-reference */
  currentItemId?: number;
  /**
   * Component IDs already chosen on OTHER rows. The DB has a UNIQUE
   * (served_item_id, component_item_id) index, so picking the same ingredient
   * twice fails the whole create — hide them instead of letting it happen.
   */
  takenItemIds?: number[];
  onRemove: () => void;
}

export function CreateRecipeRow({ index, control, setValue, register, currentItemId, takenItemIds = [], onRemove }: Props) {
  const [comboOpen, setComboOpen] = useState(false);
  const [comboSearch, setComboSearch] = useState("");

  const componentItemId = useWatch({ control, name: `recipeComponents.${index}.componentItemId` });
  const consumptionMode = useWatch({ control, name: `recipeComponents.${index}.consumptionMode` }) ?? "tracked";

  const { data: allItems = [] } = useListInventoryItems({ status: "Active" });

  // Filter: exclude the current item (edit mode self-reference), items that
  // already have a recipe (depth-1 guard), and ingredients another row already
  // claimed (this row's own pick stays listed so it renders as checked).
  const candidateItems = allItems.filter(
    (it: InventoryItem) =>
      it.id !== currentItemId &&
      !it.hasRecipe &&
      (it.id === componentItemId || !takenItemIds.includes(it.id))
  );

  const filteredCandidates = candidateItems.filter((it: InventoryItem) =>
    it.name.toLowerCase().includes(comboSearch.toLowerCase())
  );

  const selectedItem = allItems.find((it: InventoryItem) => it.id === componentItemId);
  // Unit the serve is counted in — shown inside the qty field so a bare "1" is
  // never ambiguous (1 gram vs 1 pack of a 560-gram item).
  const unitLabel = stockOutUnitLabel(selectedItem);

  return (
    <div className="rounded border p-3 space-y-2 bg-muted/20">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className="text-xs text-muted-foreground mb-1">Ingredient <span className="text-destructive">*</span></p>
          <Popover open={comboOpen} onOpenChange={setComboOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                className="w-full justify-between text-sm"
              >
                <span className={cn("truncate", !selectedItem && "text-muted-foreground")}>
                  {selectedItem ? (
                    <>
                      {selectedItem.name}
                      {selectedItem.brand ? ` · ${selectedItem.brand}` : ""}
                      {specsToLine(selectedItem.specs) ? ` · ${specsToLine(selectedItem.specs)}` : ""}
                    </>
                  ) : "Search ingredients…"}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[360px] p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search by name…"
                  value={comboSearch}
                  onValueChange={setComboSearch}
                />
                <CommandList>
                  <CommandEmpty>No items found.</CommandEmpty>
                  <CommandGroup>
                    {filteredCandidates.map((it: InventoryItem) => {
                      const specs = specsToLine(it.specs);
                      const availNum =
                        it.inventoryTracked === false
                          ? null
                          : (it as unknown as { variantStockTotal?: number | string | null }).variantStockTotal != null
                            ? Number((it as unknown as { variantStockTotal?: number | string | null }).variantStockTotal)
                            : it.currentStock != null
                              ? Number(it.currentStock)
                              : null;
                      const avail = availNum != null && Number.isFinite(availNum) ? availNum : null;
                      return (
                        <CommandItem
                          key={it.id}
                          value={String(it.id)}
                          onSelect={() => {
                            setValue(`recipeComponents.${index}.componentItemId`, it.id);
                            // Qty per serve is expressed in THIS ingredient's stock-out
                            // unit, so re-seed it from the item whenever the pick changes —
                            // a qty carried over from a different ingredient is meaningless.
                            setValue(
                              `recipeComponents.${index}.qtyPerServe`,
                              defaultQtyPerServe(it),
                            );
                            setComboSearch("");
                            setComboOpen(false);
                          }}
                          className="py-2"
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4 shrink-0",
                              componentItemId === it.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <div className="flex min-w-0 flex-1 flex-col">
                            <span className="text-sm truncate">
                              {it.name}
                              {it.brand ? ` · ${it.brand}` : ""}
                            </span>
                            {(specs || it.unitOfMeasurement) && (
                              <span className="text-[11px] text-muted-foreground truncate">
                                {[specs, `(${it.unitOfMeasurement})`].filter(Boolean).join(" · ")}
                              </span>
                            )}
                          </div>
                          {avail != null && (
                            <span
                              className={cn(
                                "ml-2 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums",
                                avail <= 0
                                  ? "bg-destructive/10 text-destructive"
                                  : "bg-emerald-50 text-emerald-700",
                              )}
                            >
                              {avail} in stock
                            </span>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Qty per serve <span className="text-destructive">*</span></p>
          <div className="relative">
            <Input
              type="number"
              step="0.001"
              min="0.001"
              placeholder="e.g. 1.000"
              className={cn(unitLabel && "pr-16")}
              {...register(`recipeComponents.${index}.qtyPerServe`, { valueAsNumber: true })}
            />
            {unitLabel && (
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                {unitLabel}
              </span>
            )}
          </div>
          {selectedItem && stockOutHint(selectedItem) && (
            <p className="text-[11px] text-muted-foreground mt-1">{stockOutHint(selectedItem)}</p>
          )}
        </div>
      </div>
      <div>
        <p className="text-xs text-muted-foreground mb-1">Consumption Mode</p>
        <div className="inline-flex rounded-md border bg-background p-0.5 text-xs">
          <button
            type="button"
            className={cn(
              "px-3 py-1 rounded-sm transition-colors",
              consumptionMode === "tracked"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
            onClick={() => setValue(`recipeComponents.${index}.consumptionMode`, "tracked")}
          >
            Tracked
          </button>
          <button
            type="button"
            className={cn(
              "px-3 py-1 rounded-sm transition-colors",
              consumptionMode === "estimated"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
            onClick={() => setValue(`recipeComponents.${index}.consumptionMode`, "estimated")}
          >
            Estimated
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">
          {consumptionMode === "estimated"
            ? "Known dependency, but stock is NOT auto-decremented on serve (e.g. ink per print)."
            : "Decrements component stock on each serve."}
        </p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground mb-1">Notes</p>
        <Textarea
          placeholder="Optional — e.g. 'boiled before adding'"
          className="h-16 resize-none"
          {...register(`recipeComponents.${index}.notes`)}
        />
      </div>
      <div className="flex justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={onRemove} className="text-destructive hover:text-destructive text-xs">
          Remove component
        </Button>
      </div>
    </div>
  );
}
